import { Icon } from '@gftg/ui';
import { Link } from 'react-router';
import type { PublicEvent } from '../lib/queries';
import { derivedSubtitle, LOCKED_NOTE, ROUTES, tileKey, type Tile } from '../lib/tiles';
import { LIST_ROW } from './states';
import { TileIcon } from './TileIcon';

/** The conference itself, in the organizer's order, followed by the links that leave the app. */
export function ConferenceList({ tiles, event }: { tiles: Tile[]; event: PublicEvent }) {
  return (
    <ul className="rounded-card divide-y divide-border-row overflow-hidden border border-border bg-surface">
      {tiles.map((tile) => (
        <li key={tile.id}>
          <Row tile={tile} event={event} />
        </li>
      ))}
    </ul>
  );
}

function Row({ tile, event }: { tile: Tile; event: PublicEvent }) {
  const subtitle = derivedSubtitle(tile, event);

  if (tile.targetType === 'external' && tile.externalUrl) {
    return (
      <a href={tile.externalUrl} target="_blank" rel="noreferrer noopener" className={LIST_ROW}>
        <Label tile={tile} subtitle={subtitle} fallbackIcon="external" />
        {/* HOME-05: the 12px mark is the kit's promise that this leaves the app. */}
        <Icon name="external" size={12} className="shrink-0 text-hairline" />
        <span className="sr-only">Opens outside the app</span>
      </a>
    );
  }

  const key = tileKey(tile);
  const route = key ? ROUTES[key] : undefined;

  if (route) {
    return (
      <Link to={route} className={LIST_ROW}>
        <Label tile={tile} subtitle={subtitle} />
        <Icon name="chevron-right" size={18} className="shrink-0 text-hairline" />
      </Link>
    );
  }

  return (
    <div className={LIST_ROW} aria-disabled="true">
      <Label tile={tile} subtitle={(key && LOCKED_NOTE[key]) ?? 'Not available yet'} muted />
    </div>
  );
}

function Label({
  tile,
  subtitle,
  muted = false,
  fallbackIcon,
}: {
  tile: Tile;
  subtitle: string | null;
  muted?: boolean;
  fallbackIcon?: 'external';
}) {
  return (
    <>
      <TileIcon
        iconKey={tile.iconKey}
        fallback={fallbackIcon}
        className={muted ? 'shrink-0 text-ink-4' : 'shrink-0 text-ink-2'}
      />
      <span className="min-w-0 flex-1">
        <span
          className={`text-row block truncate font-semibold ${muted ? 'text-ink-4' : 'text-ink'}`}
        >
          {tile.label}
        </span>
        {subtitle && (
          <span className="text-subtitle mt-0.5 block truncate text-ink-3">{subtitle}</span>
        )}
      </span>
    </>
  );
}
